// chat/actions.ts — the chat control executor (cloned from Oriora Expense's src/chat/actions.ts,
// itself cloned from Oriora Content). The client router (chat/brain.ts) maps ONE user message →
// ONE ChatAction; this file EXECUTES that action against the on-device store (src/store.ts).
// Everything here is LOCAL SQLite over Tauri IPC — no network, no billing — EXCEPT "remember",
// which reuses the app's ONE already-live AI endpoint (weaveRemembrance in ../brain, unchanged).
//
// HITL discipline (mirrors Expense/Content): the destructive action (delete_moment) is NEVER
// executed from a chat turn on its own — the Chat UI holds it and requires an explicit Confirm
// tap first. This executor just performs the delete once the UI hands it back.
//
// GRIEF SOFT FENCE: "remember" calls store.computeSignals + weaveRemembrance EXACTLY as the
// existing RemembranceCard does — that call already carries the pre-LLM distress fence and the
// server-side grief-fence prompt guardrails. Nothing here adds a second AI call or bypasses them.

import { store, normalizePlace, prettyDate, todayLocal, type Moment, type MomentPatch, type PetPatch } from "../store";
import { exportBackup, importBackup } from "../backup";
import { invoke } from "../tauri-web";
import { saveCopyNow, importFromFile } from "../localFolder";
const LOCAL_SLUG = "forever-paw";
import { cloudBackupSave, cloudBackupLoad, cloudBackupStatus, cloudStoreSave } from "../brain";
import { addInstruction } from "./brain";
import { weaveRemembrance, BrainError } from "../brain";
import { libraryAdd, libraryList, libraryRemove, libraryMove, libraryMerge } from "../library";

// A compact moment row rendered inline in a chat bubble (filter results).
export interface FilterRow {
  id: string;
  happened_on: string;
  title: string;
  place: string;
  is_milestone: boolean;
}

export interface FilterQuery {
  place?: string;
  milestone?: boolean;
  from?: string; // YYYY-MM-DD inclusive
  to?: string;   // YYYY-MM-DD inclusive
  keyword?: string;
}

export type ChatAction =
  | { kind: "none"; message?: string }
  | { kind: "add_moment"; happened_on: string; title: string; caption?: string; place?: string; is_milestone?: boolean }
  | { kind: "edit_moment"; id: string; patch: MomentPatch; label?: string }
  | { kind: "delete_moment"; id: string; label?: string } // UI confirms before this reaches executeAction
  | { kind: "filter"; query: FilterQuery; label?: string }
  | { kind: "edit_pet"; patch: PetPatch; label?: string }
  | { kind: "remember" } // reuses the ONE live AI endpoint (weaveRemembrance) — no new call shape
  | { kind: "backup_save" }
  | { kind: "backup_status" }
  | { kind: "backup_restore"; provider?: "github" | "gdrive" } // UI confirms before executeAction (replaces all data)
  | { kind: "save_local_copy" }
  | { kind: "import_file" }
  | { kind: "save_to_cloud"; contentKind: "conversation" | "note"; note?: string; destination?: "github" | "gdrive" }
  | { kind: "remember_preference"; instruction: string }
  // ── ARTIFACT TOOLS — the customer's library (fleet-wide) ────────────────────
  | { kind: "library_save"; title: string; itemKind: "lesson" | "plan" | "note"; content: string; folder?: string }
  | { kind: "library_list"; folder?: string }
  | { kind: "library_remove"; id: string; label?: string }   // UI confirms first
  | { kind: "library_move"; id: string; folder: string; label?: string }
  | { kind: "library_merge"; ids: string[]; title: string; label?: string };  // UI confirms first

export interface ActionResult {
  ok: boolean;
  message: string;   // shown back in the chat
  changed?: boolean; // a moment/pet changed → the Home screen should reload
  rows?: FilterRow[]; // filter matches to render inline
}

/** Local (no-network) filter over already-loaded moments — mirrors Expense's applyFilter shape. */
export function applyMomentFilter(rows: Moment[], q: FilterQuery): Moment[] {
  let out = rows.slice();
  if (q.from) out = out.filter((m) => m.happened_on >= q.from!);
  if (q.to) out = out.filter((m) => m.happened_on <= q.to!);
  if (q.place) { const p = normalizePlace(q.place); out = out.filter((m) => m.place.includes(p)); }
  if (q.milestone !== undefined) out = out.filter((m) => m.is_milestone === q.milestone);
  if (q.keyword) {
    const k = q.keyword.toLowerCase();
    out = out.filter((m) => m.title.toLowerCase().includes(k) || m.caption.toLowerCase().includes(k));
  }
  return out.sort((a, b) => (a.happened_on < b.happened_on ? 1 : a.happened_on > b.happened_on ? -1 : 0));
}

export async function executeAction(a: ChatAction, ctx?: { transcript?: string }): Promise<ActionResult> {
  try {
    switch (a.kind) {
      case "none":
        return { ok: true, message: a.message ?? "" };

      case "add_moment": {
        const m = await store.createMoment({
          happened_on: a.happened_on,
          title: a.title,
          caption: a.caption ?? "",
          place: a.place ?? "",
          is_milestone: a.is_milestone ?? false,
        });
        const when = m.happened_on ? ` on ${prettyDate(m.happened_on)}` : "";
        const where = m.place ? ` · ${m.place}` : "";
        const flag = m.is_milestone ? " (milestone)" : "";
        return { ok: true, message: `Saved "${m.title || "a moment"}"${when}${where}${flag}.`, changed: true };
      }

      case "edit_moment":
        await store.updateMoment(a.id, a.patch);
        return { ok: true, message: `Updated ${a.label ?? "the moment"}.`, changed: true };

      case "delete_moment":
        await store.deleteMoment(a.id);
        return { ok: true, message: `Deleted ${a.label ?? "the moment"}.`, changed: true };

      case "filter": {
        const all = await store.listMoments({ limit: 50000 });
        const rows = applyMomentFilter(all, a.query);
        const forLabel = a.label ? ` for "${a.label}"` : "";
        const rendered: FilterRow[] = rows.slice(0, 50).map((m) => ({
          id: m.id,
          happened_on: m.happened_on,
          title: m.title || "(untitled)",
          place: m.place,
          is_milestone: m.is_milestone,
        }));
        return {
          ok: true,
          message: `${rows.length} moment${rows.length !== 1 ? "s" : ""}${forLabel}` + (rows.length > 50 ? " (showing first 50)" : ""),
          rows: rendered,
        };
      }

      case "edit_pet": {
        const p = await store.upsertPet(a.patch);
        return { ok: true, message: `Updated ${p.name || "your pet"}'s info.`, changed: true };
      }

      case "remember": {
        // EXACTLY the RemembranceCard path: compute the local slice, then the ONE live AI call.
        const signals = await store.computeSignals(todayLocal());
        const r = await weaveRemembrance(signals);
        return { ok: true, message: r.remembrance || "Nothing to weave yet.", changed: false };
      }

      case "backup_save": {
        let provider: string;
        try {
          ({ provider } = await cloudBackupSave(JSON.parse(await exportBackup())));
        } catch (e) {
          // Photo-heavy backup past the standard path's size limit → the device-direct GitHub
          // save (any size, straight from this device, never through Oriora). Chat can DO this
          // one itself when GitHub is already connected in the backup menu; it can't run the
          // one-time OAuth login, so when not connected it points there by name instead.
          if (e instanceof BrainError && e.code === "too_large") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected) {
              await invoke("backup_to_github_token");
              return { ok: true, message: "Your backup is photo-heavy, so I saved it straight from this device to your GitHub — any size works that way, and it never passes through Oriora." };
            }
            return { ok: false, message: 'This backup is too large for the standard path (photos add up). One-time fix: open the backup menu (☁ icon, top right) → "Log in with GitHub" — after that, large backups save directly from this device, and I can run them for you from chat.' };
          }
          throw e;
        }
        const name = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "cloud";
        return { ok: true, message: "Backed up your whole collection to your " + name + " — restore it on any device, any time." };
      }

      case "backup_status": {
        // The RESTORE STORY in words — same ground truth the panel's status line shows.
        const s = await cloudBackupStatus();
        if (s.connected.length === 0) {
          const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
          if (deviceGh) {
            return { ok: true, message: "GitHub is logged in on this device (backup menu), so backups save and restore directly from here — any size, never passing through Oriora. Optionally, connecting a cloud at orioralabs.com → Settings → Connections adds the everyday path all your Oriora apps share." };
          }
          return { ok: true, message: 'Nothing is connected yet. Two options: connect GitHub or Google Drive once at orioralabs.com → Settings → Connections (all your Oriora apps back up there), or open the backup menu here (☁ icon) → "Log in with GitHub" — that one stays on this device only, and handles any size, photos included.' };
        }
        const name = (p: string) => (p === "gdrive" ? "Google Drive" : "GitHub");
        const when = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : null);
        const lines: string[] = [];
        const w = s.writes_to;
        if (w) {
          const slot = s.slots[w];
          lines.push(`Your backups write to ${name(w)} (connected in your Oriora account)` +
            (slot?.backup_exists ? ` — last backup ${when(slot.updated_at)}.` : " — no backup saved yet."));
        }
        for (const p of s.connected.filter((c) => c !== w)) {
          const slot = s.slots[p];
          lines.push(`${name(p)} is also connected but not used for new backups (${name(w!)} takes priority)` +
            (slot?.backup_exists ? ` — it still holds an older backup from ${when(slot.updated_at)}.` : "."));
        }
        const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
        if (deviceGh) {
          lines.push("GitHub is also logged in on this device, so large photo-heavy backups save and restore directly from here — any size, never through Oriora.");
        }
        lines.push('Say "restore my moments" and I\'ll read the backup back in — or name a source, like "restore from Drive".');
        return { ok: true, message: lines.join("\n") };
      }

      case "backup_restore": {
        const src = a.provider ? (a.provider === "gdrive" ? "Google Drive" : "GitHub") : "cloud";
        let st;
        try {
          st = await cloudBackupLoad(a.provider);
        } catch (e) {
          // Large-backup handoff: if GitHub is connected on this device, chat pulls it directly
          // itself (the user already confirmed the REPLACE before this executes); it can't run the
          // one-time OAuth login, so when not connected it points at the button by name instead.
          if (e instanceof BrainError && e.code === "unavailable") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected && (!a.provider || a.provider === "github")) {
              await invoke("restore_from_github_token");
              return { ok: true, message: "Your backup was too large for the standard path, so I pulled it straight from your GitHub onto this device — your data is back.", changed: true };
            }
            return { ok: false, message: `Reading your ${src} backup through Oriora didn't work — if your backup is large (it can be, with photos), use the backup menu (☁ icon, top right) → "Log in with GitHub", which backs up and restores any size directly on this device.` };
          }
          throw e;
        }
        if (!st.connected) return { ok: false, message: "Connect GitHub or Google Drive in your Oriora account first (Settings → Service connections)." };
        if (!st.data) return { ok: false, message: a.provider ? `No backup found in your ${src} yet.` : 'No cloud backup found yet — say "back up" first.' };
        await importBackup(JSON.stringify(st.data));
        return { ok: true, message: `Restored from your ${st.provider === "gdrive" ? "Google Drive" : "GitHub"} backup.`, changed: true };
      }

      case "save_local_copy": {
        const saved = await saveCopyNow(LOCAL_SLUG);
        return saved
          ? { ok: true, message: "Saved a copy of your data to the folder you picked — it's yours to keep or move." }
          : { ok: true, message: "Cancelled — no copy saved." };
      }

      case "import_file": {
        const json = await importFromFile();
        if (json === null) return { ok: true, message: "No file chosen — nothing imported." };
        await importBackup(json);
        return { ok: true, message: "Imported your data from the file — this device now matches that backup.", changed: true };
      }

      case "save_to_cloud": {
        // Save a single note or the whole conversation to the customer's OWN cloud (their GitHub/Drive),
        // distinct from backup_save (the whole moments collection). Oriora retains nothing.
        const content = a.contentKind === "conversation" ? (ctx?.transcript ?? "") : (a.note ?? "");
        if (!content.trim()) {
          return { ok: false, message: a.contentKind === "conversation" ? "There's nothing to save yet — chat with me a bit first, then ask again." : "Tell me what to save and I'll store it." };
        }
        const { provider } = await cloudStoreSave(content, { name: a.contentKind, kind: a.contentKind, destination: a.destination });
        const where = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "your cloud";
        const what = a.contentKind === "conversation" ? "our conversation" : "your note";
        return { ok: true, message: `Saved ${what} to your ${where} — it's yours to keep.` };
      }

      case "remember_preference": {
        addInstruction(a.instruction);
        return { ok: true, message: "Noted — from now on: " + a.instruction };
      }

      // ── ARTIFACT TOOLS (the library) — device-only, fleet-wide ──────────────
      case "library_save": {
        const _item = libraryAdd(a.title, a.itemKind, a.content, a.folder ?? "");
        return { ok: true, message: `Saved "${_item.title}" to your library${_item.folder ? ` (folder: ${_item.folder})` : ""} — on this device, yours.`, changed: true };
      }
      case "library_list": {
        const _items = libraryList(a.folder);
        if (!_items.length) return { ok: true, message: a.folder ? `Nothing in "${a.folder}" yet.` : "Your library is empty — ask me to write you something and I'll keep it here." };
        return { ok: true, message: (a.folder ? `In "${a.folder}":\n` : "Your library:\n") + _items.map((i) => `• ${i.title} — ${i.kind}${i.folder ? ` · ${i.folder}` : ""}`).join("\n") };
      }
      case "library_remove":
        return libraryRemove(a.id)
          ? { ok: true, message: `Removed ${a.label ?? "it"} from your library.`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_move":
        return libraryMove(a.id, a.folder)
          ? { ok: true, message: `Moved ${a.label ?? "it"} to "${a.folder}".`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_merge": {
        const _merged = libraryMerge(a.ids, a.title);
        return _merged
          ? { ok: true, message: `Merged into "${_merged.title}" — the originals are folded in.`, changed: true }
          : { ok: false, message: "I need at least two existing items to merge." };
      }


      default:
        return { ok: false, message: "I didn't catch that — try again?" };
    }
  } catch (e) {
    if (e instanceof BrainError) return { ok: false, message: e.message };
    return { ok: false, message: `That didn't work: ${e instanceof Error ? e.message : String(e)}` };
  }
}
